import { Plus } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';

import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { SegTabs } from '@/components/ui/SegTabs';
import { segPanelId, segTabId } from '@/components/ui/seg-tabs-ids';
import { usePermissionState } from '@/hooks/usePermission';

import { CustomFieldsPanel } from './components/CustomFieldsPanel';
import { DocumentTypesPanel } from './components/DocumentTypesPanel';
import { NormsPanel } from './components/NormsPanel';
import { ProcessAreasPanel } from './components/ProcessAreasPanel';
import { CatalogSkeleton } from './components/CatalogSkeleton';
import { ENTITY_COPY } from './copy';
import type { CatalogEntity } from './types';
import { CATALOG_TABS, useCatalogView, type CatalogTab } from './use-catalog-view';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Configuración' },
  { label: 'Catálogos', strong: true },
];

const TAB_ENTITY: Record<CatalogTab, CatalogEntity> = {
  tipos: 'document_type',
  procesos: 'process_area',
  normas: 'norm',
  campos: 'custom_field',
};

const ID_PREFIX = 'catalogo';

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin permiso confirmado, las migas y el encabezado no nombran la pantalla.
  const perm = usePermissionState('quality:catalogs:manage');
  return (
    <>
      <GatedChrome state={perm.state} breadcrumb={BREADCRUMB} navContext="Catálogos" />
      {children}
    </>
  );
}

/** Configuración › Catálogos (QA F1 #13): tipos de documento, procesos y normas con
 *  sus cláusulas, administrados por Calidad sin ayuda del proveedor. Nada se borra:
 *  lo desactivado deja de ofrecerse y lo que ya lo usa se conserva. */
export function Catalogs(): ReactElement {
  const perm = usePermissionState('quality:catalogs:manage');
  const view = useCatalogView();
  const [creating, setCreating] = useState(false);
  const { tab } = view.view;

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'denied') {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }

  const copy = ENTITY_COPY[TAB_ENTITY[tab]];
  const panelProps = {
    view,
    creating,
    onCreate: () => setCreating(true),
    onCloseCreate: () => setCreating(false),
  };

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Catálogos</h1>
          <p style={{ maxWidth: '70ch' }}>
            Tipos de documento, procesos, normas y campos propios de la empresa activa.
          </p>
        </div>
        {perm.state === 'granted' && (
          <button className="btn" type="button" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" strokeWidth={1.9} />
            {copy.newLabel}
          </button>
        )}
      </div>
      <SegTabs
        tabs={CATALOG_TABS}
        value={tab}
        onChange={(next) => {
          setCreating(false);
          view.setTab(next);
        }}
        label="Catálogo"
        idPrefix={ID_PREFIX}
      />
      <div
        className="tabpanel"
        role="tabpanel"
        id={segPanelId(ID_PREFIX, tab)}
        aria-labelledby={segTabId(ID_PREFIX, tab)}
      >
        {perm.state === 'loading' ? (
          <CatalogSkeleton />
        ) : tab === 'tipos' ? (
          <DocumentTypesPanel {...panelProps} />
        ) : tab === 'procesos' ? (
          <ProcessAreasPanel {...panelProps} />
        ) : tab === 'normas' ? (
          <NormsPanel {...panelProps} />
        ) : (
          <CustomFieldsPanel {...panelProps} />
        )}
      </div>
    </Frame>
  );
}
